import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { message } from "antd";
import dayjs from "dayjs";
import "react-data-grid/lib/styles.css";
import "./HarvestEntryGrid.css";

import { BACKEND_DATE_FORMAT } from "../../../consts/index.js";

import { HarvestEntryLayout } from "./HarvestEntryLayout.jsx";
import { useHarvestEntryLayout } from "./useHarvestEntryLayout.js";
import { useHarvestEntryUndoRedo } from "./useHarvestEntryUndoRedo.js";
import { useHarvestEntryColumns } from "./useHarvestEntryColumns.jsx";
import { useHarvestEntryBulkActions } from "./useHarvestEntryBulkActions.js";
import { useHarvestEntryEditors } from "./useHarvestEntryEditors.jsx";
import { useHarvestEntryVisuals } from "./useHarvestEntryVisuals.jsx";
import { useHarvestEntryRows } from "./useHarvestEntryRows.js";
import { useHarvestEntryStatus } from "./useHarvestEntryStatus.js";
import { useHarvestEntryFilters } from "./useHarvestEntryFilters.js";
import { useHarvestEntrySave } from "./useHarvestEntrySave.js";
import { useHarvestEntryColumnWidths } from "./useHarvestEntryColumnWidths.js";
import { useHarvestEntryStatusPill } from "./useHarvestEntryStatusPill.jsx";
import { useHarvestEntryNavigation } from "./useHarvestEntryNavigation.js";
import { naturalCompare } from "./harvestEntryHelpers.js";
import { getHarvestEntryConfig } from "./harvestEntryConfigService.js";
import { STATUS } from "./harvestEntryConstants.js";

// ---------- component ----------
const HarvestEntryPage = ({
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  siteData,
  setSiteData,
  currentSite,
  setCurrentSite,
  siteLoading,
  setSiteLoading,
  currentCrop,
  setCurrentCrop,
  cropLoading,
  setCropLoading,
  cropData,
  setCropData,
  searchParams,
  setSearchParams,
  settingDefaults,
  setSettingDefaults,
}) => {
  const [messageApi, contextHolder] = message.useMessage();

  const gridRef = useRef(null);
  const gridWrapRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const UNHARVESTED_SAVE_API_CONCURRENCY = 6;
  const HARVESTED_SAVE_API_CONCURRENCY = 10;

  const {
    gridWidth,
    gridHeight,
    isFullscreen,
    isPseudoFullscreen,
    toggleFullscreen,
    exitPseudoFullscreen,
    getGridPopupContainer,
    isDesktop,
    FULLSCREEN_Z,
    DROPDOWN_Z,
    GRID_MIN_HEIGHT,
  } = useHarvestEntryLayout({ gridWrapRef, sidebarOpen });

  const [greenhouseData, setGreenhouseData] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  const [selectedGreenhouses, setSelectedGreenhouses] = useState([]);
  const [greenHouseLabel, setGreenHouseLabel] = useState("Greenhouse");
  const [blockLabel, setBlockLabel] = useState("Row");
  const [siteMetadata, setSiteMetadata] = useState({});

  const [loading] = useState(false);
  const [harvestConfig, setHarvestConfig] = useState(null);
  const [harvestConfigError, setHarvestConfigError] = useState(false);

  const [defaultHarvestDateISO, setDefaultHarvestDateISO] = useState(() =>
    dayjs().format(BACKEND_DATE_FORMAT),
  );

  // Top controls (kept as-is)
  const [statusFilter] = useState("all");
  const [searchText] = useState("");

  const [bayRowFilter, setBayRowFilter] = useState([]);

  const [statusColFilterValue, setStatusColFilterValue] = useState("Planted"); // "" means All
  const [sortColumns, setSortColumns] = useState([]);

  useEffect(() => {
    setBayRowFilter((prev) => (prev.length ? [] : prev));
  }, [statusFilter, statusColFilterValue]);

  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());

  const clearRowSelection = useCallback(() => {
    setSelectedRowIds(new Set());
  }, []);

  const [bulkActionsOpen, setBulkActionsOpen] = useState(false);

  const [yieldKeyNameForTable, setYieldKeyNameForTable] = useState("Yield");
  const [weightKeyNameForTable, setWeightKeyNameForTable] = useState("Weight");
  const [showForecastWeightColumn, setShowForecastWeightColumn] =
    useState(false);

  const yieldLabel = yieldKeyNameForTable;
  const weightLabel = weightKeyNameForTable;
  const showWeight = showForecastWeightColumn;

  const orgId = currentOrg?.id ?? currentOrg ?? null;
  const siteId = currentSite?.id ?? currentSite ?? null;
  const cropId = currentCrop?.id ?? currentCrop ?? null;

  const resetHistoryRef = useRef(() => {});

  const { rows, setRows, rowsRef, activeGreenhouseIds } = useHarvestEntryRows({
    orgId,
    siteId,
    cropId,
    greenhouseData,
    selectedGreenhouses,
    setSelectedRowIds,
    setBayRowFilter,
    resetHistoryRef,
    naturalCompare,
  });

  const {
    undoStack,
    redoStack,
    undo,
    redo,
    pushUndoSnapshot,
    areRowsEquivalentForHistory,
    isApplyingHistoryRef,
    historySessionRef,
    resetHistory,
  } = useHarvestEntryUndoRedo({ rowsRef, setRows });

  useLayoutEffect(() => {
    resetHistoryRef.current = resetHistory;
  }, [resetHistory]);

  // ---- metadata sync ----
  useEffect(() => {
    if (!currentSite || !currentCrop) return;

    const cropMeta = currentSite?.metadata?.[currentCrop.name] || {};
    setSiteMetadata(cropMeta);

    setYieldKeyNameForTable(
      cropMeta.FORECAST_YIELD_KEY_NAME_FOR_TABLE || "Yield",
    );
    setWeightKeyNameForTable(
      cropMeta.FORECAST_WEIGHT_KEY_NAME_FOR_TABLE || "Weight",
    );
    setShowForecastWeightColumn(cropMeta.SHOW_FORECAST_WEIGHT_COLUMN === true);
  }, [currentSite, currentCrop]);

  // ---- load harvest entry config ----
  useEffect(() => {
    if (!currentSite || !currentCrop) return;
    setHarvestConfig(null);
    setHarvestConfigError(false);
    let ignore = false;
    const sid = currentSite?.id ?? currentSite;
    const cid = currentCrop?.id ?? currentCrop;
    getHarvestEntryConfig(sid, cid, orgId)
      .then((cfg) => {
        if (!ignore && cfg) setHarvestConfig(cfg);
      })
      .catch((e) => {
        if (!ignore) {
          console.error("Failed to load harvest entry config:", e);
          setHarvestConfigError(true);
        }
      });
    return () => { ignore = true; };
  }, [currentSite, currentCrop, orgId]);

  const {
    getEffectiveYield,
    getEffectiveWeight,
    isRowDiscarded,
    getBackendStatus,
    getBackendStatusText,
    getEffectiveStatus,
    getEffectiveStatusText,
    rowHasPendingChanges,
  } = useHarvestEntryStatus({ showWeight });

  const pairKeyOfRow = useCallback(
    (r) => `${r.bay_name}__${r.row_name}`,
    [],
  );

  // ---------- Harvest refresh hook from Dropdowns ----------
  const refreshHarvestRef = useRef(null);
  const registerHarvestRefresh = useCallback((fn) => {
    refreshHarvestRef.current = fn;
  }, []);

  const {
    statusOptions,
    bayRowTreeData,
    bayRowExpandedKeys,
    setBayRowExpandedKeys,
    handleBayRowFilterChange,
    visibleRows,
  } = useHarvestEntryFilters({
    rows,
    activeGreenhouseIds,
    statusFilter,
    searchText,
    bayRowFilter,
    setBayRowFilter,
    statusColFilterValue,
    sortColumns,
    getBackendStatus,
    getBackendStatusText,
    getEffectiveStatusText,
    rowHasPendingChanges,
    pairKeyOfRow,
    naturalCompare,
  });

  // Tracks whether any cell editor has unsaved text being typed.
  // Kept separate from `rows` so updating it does NOT change the rows prop
  // to DataGrid, which would cause react-data-grid to lose editor focus.
  const [isEditorDirty, setIsEditorDirty] = useState(false);

  const hasAnyPendingEdits = useMemo(
    () => isEditorDirty || rows.some(rowHasPendingChanges),
    [isEditorDirty, rows, rowHasPendingChanges],
  );

  const ensurePendingDate = useCallback(
    (row) => {
      if (row.pendingHarvestDate || row.harvest_date) return row;
      return { ...row, pendingHarvestDate: defaultHarvestDateISO };
    },
    [defaultHarvestDateISO],
  );

  const rowsById = useMemo(() => {
    const m = new Map();
    for (const r of rows) m.set(r.id, r);
    return m;
  }, [rows]);

  const {
    bulkMenuItems,
  } = useHarvestEntryBulkActions({
    selectedRowIds,
    rowsById,
    getBackendStatus,
    rowHasPendingChanges,
    ensurePendingDate,
    showWeight,
    pushUndoSnapshot,
    rowsRef,
    setRows,
    clearRowSelection,
    messageApi,
  });

  const { savingAll, handleSaveVisible } = useHarvestEntrySave({
    rows,
    rowsRef,
    setRows,
    rowHasPendingChanges,
    showWeight,
    statusColFilterValue,
    orgId,
    siteId,
    cropId,
    clearRowSelection,
    messageApi,
    refreshHarvestRef,
    unharvestConcurrency: UNHARVESTED_SAVE_API_CONCURRENCY,
    harvestConcurrency: HARVESTED_SAVE_API_CONCURRENCY,
    harvestConfig,
    harvestConfigError,
  });

  const { computeWidths } = useHarvestEntryColumnWidths({
    gridWidth,
    showWeight,
  });

  const fillCellPositionRef = useRef(null);

  const { onKeyDown, move, onCellClick } = useHarvestEntryNavigation({
    gridRef,
    visibleRowsLength: visibleRows.length,
    isDesktop,
    showWeight,
    undo,
    redo,
    isPseudoFullscreen,
    exitPseudoFullscreen,
    fillCellPositionRef,
  });

  const { statusPill } = useHarvestEntryStatusPill({
    rowHasPendingChanges,
    getEffectiveStatus,
  });

  const {
    renderDateEditCell,
    renderNumberEditCell,
    renderStatusEditCell,
    renderTubWeightEditCell,
    renderDiscardReasonEditCell,
    renderDiscardNoteEditCell,
  } = useHarvestEntryEditors({
    ensurePendingDate,
    move,
    setDefaultHarvestDateISO,
    getEffectiveStatus,
    getBackendStatus,
    isRowDiscarded,
    showWeight,
    getGridPopupContainer,
    dropdownZIndex: DROPDOWN_Z,
    harvestConfig,
    rowsRef,
    setIsEditorDirty,
  });

  const [fillColumnKey, setFillColumnKey] = useState(null);
  const [isFillDragging, setIsFillDragging] = useState(false);
  const fillResetTimerRef = useRef(null);
  const fillUpdateRef = useRef(false);

  const scheduleFillReset = useCallback(() => {
    if (fillResetTimerRef.current) {
      clearTimeout(fillResetTimerRef.current);
    }
    fillResetTimerRef.current = setTimeout(() => {
      setIsFillDragging(false);
      fillUpdateRef.current = false;
    }, 100);
  }, []);

  const onRowsChange = useCallback(
    (nextVisibleRows, { indexes }) => {
      const updatedById = new Map();
      for (const i of indexes) {
        const updated = nextVisibleRows[i];
        if (updated?.id) updatedById.set(updated.id, updated);
      }

      // Synchronously keep rowsRef current so handleSaveVisible always
      // reads the latest data even before React processes the setRows batch.
      rowsRef.current = rowsRef.current.map((r) => updatedById.get(r.id) ?? r);

      // Editor committed — rows will now reflect the change, so the Save button
      // stays enabled via rows.some(rowHasPendingChanges).
      setIsEditorDirty(false);

      setRows((prev) => {
        const nextAll = prev.map((r) => updatedById.get(r.id) ?? r);

        if (areRowsEquivalentForHistory(prev, nextAll)) return prev;
        if (isApplyingHistoryRef.current) return nextAll;

        if (!historySessionRef.current.active) {
          pushUndoSnapshot(prev);
          historySessionRef.current.active = true;
        }

        if (historySessionRef.current.timer) {
          clearTimeout(historySessionRef.current.timer);
        }
        historySessionRef.current.timer = setTimeout(() => {
          historySessionRef.current.active = false;
          historySessionRef.current.timer = null;
        }, 800);

        return nextAll;
      });

      if (fillUpdateRef.current) {
        fillUpdateRef.current = false;
        scheduleFillReset();
      }

    },
    [areRowsEquivalentForHistory, pushUndoSnapshot, scheduleFillReset],
  );

  useEffect(() => {
    if (!showWeight && fillColumnKey === "pendingWeight") {
      setFillColumnKey(null);
      return;
    }
    if (!isDesktop && fillColumnKey !== null) {
      setFillColumnKey(null);
    }
  }, [fillColumnKey, isDesktop, showWeight]);

  useEffect(() => {
    if (!isFillDragging) return;
    const stop = () => {
      setIsFillDragging(false);
      scheduleFillReset();
    };
    window.addEventListener("pointerup", stop, true);
    window.addEventListener("pointercancel", stop, true);
    window.addEventListener("mouseup", stop, true);
    window.addEventListener("touchend", stop, true);
    window.addEventListener("blur", stop);
    return () => {
      window.removeEventListener("pointerup", stop, true);
      window.removeEventListener("pointercancel", stop, true);
      window.removeEventListener("mouseup", stop, true);
      window.removeEventListener("touchend", stop, true);
      window.removeEventListener("blur", stop);
    };
  }, [isFillDragging, scheduleFillReset]);

  useEffect(() => {
    return () => {
      if (fillResetTimerRef.current) {
        clearTimeout(fillResetTimerRef.current);
      }
    };
  }, []);

  const onSelectedCellChange = useCallback(({ column, row, rowIdx }) => {
    if (!isDesktop) {
      fillCellPositionRef.current = null;
      setFillColumnKey(null);
      return;
    }
    const key = column?.key;
    const editable =
      !!column?.renderEditCell &&
      (typeof column?.editable === "function"
        ? row
          ? column.editable(row)
          : false
        : column?.editable !== false);

    // Auto-open the dropdown as soon as the cell is selected — mirrors how
    // harvest date opens its picker without needing a separate Enter press.
    if (key === "pendingPloughedReasons" && editable) {
      requestAnimationFrame(() => {
        gridRef.current?.selectCell?.(
          { rowIdx, idx: column.idx },
          { enableEditor: true, shouldFocusCell: true },
        );
      });
      fillCellPositionRef.current = null;
      setFillColumnKey(null);
      return;
    }

    if (
      editable &&
      (key === "pendingYield" ||
        (showWeight && key === "pendingWeight") ||
        key === "pendingTubWeight" ||
        key === "pendingPloughedNote")
    ) {
      fillCellPositionRef.current = { rowIdx, idx: column.idx };
      setFillColumnKey(key);
      return;
    }

    fillCellPositionRef.current = null;
    setFillColumnKey(null);
  }, [isDesktop, showWeight]);

  const applyFilledNumber = useCallback(
    (row, key, value) => {
      const savedValue =
        key === "pendingYield" ? (row.yield ?? 0) : (row.weight ?? 0);

      const base = ensurePendingDate({ ...row, pendingUnharvest: false });

      let nextValue = value;
      if (nextValue === undefined || nextValue === null || Number.isNaN(value)) {
        nextValue = null;
      }

      if (nextValue !== null && nextValue === savedValue) {
        nextValue = null;
      }

      const nextRow = { ...base, [key]: nextValue };

      if (key === "pendingYield" && nextValue !== null && nextValue !== -1) {
        nextRow.pendingPloughedReasons = null;
        nextRow.pendingPloughedNote = null;
      }

      if (
        nextRow.pendingUnharvest === row.pendingUnharvest &&
        nextRow.pendingHarvestDate === row.pendingHarvestDate &&
        nextRow.pendingYield === row.pendingYield &&
        nextRow.pendingWeight === row.pendingWeight
      ) {
        return row;
      }

      return nextRow;
    },
    [ensurePendingDate],
  );

  const onFill = useCallback(
    ({ columnKey, sourceRow, targetRow }) => {
      // --- yield / weight ---
      if (columnKey === "pendingYield" || columnKey === "pendingWeight") {
        if (columnKey === "pendingWeight" && !showWeight) return targetRow;
        const sourceValue =
          columnKey === "pendingYield"
            ? getEffectiveYield(sourceRow)
            : getEffectiveWeight(sourceRow);
        const updated = applyFilledNumber(targetRow, columnKey, sourceValue);
        if (updated !== targetRow) fillUpdateRef.current = true;
        return updated;
      }

      // --- tub weight ---
      if (columnKey === "pendingTubWeight") {
        if (isRowDiscarded(targetRow) || targetRow.pendingUnharvest) return targetRow;
        const defaultTubWeight = harvestConfig?.tub_weight?.default ?? 0;
        const sourceValue =
          sourceRow.pendingTubWeight !== null && sourceRow.pendingTubWeight !== undefined
            ? sourceRow.pendingTubWeight
            : (sourceRow.weight_per_tub ?? defaultTubWeight);
        const savedValue = targetRow.weight_per_tub ?? defaultTubWeight;
        const newPendingTubWeight = sourceValue === savedValue ? null : sourceValue;
        const base = ensurePendingDate({ ...targetRow, pendingUnharvest: false });
        fillUpdateRef.current = true;
        return { ...base, pendingTubWeight: newPendingTubWeight };
      }

      // --- discard note ---
      if (columnKey === "pendingPloughedNote") {
        if (targetRow.pendingUnharvest) return targetRow;
        const targetStatus = getEffectiveStatus(targetRow);
        if (targetStatus !== STATUS.PLANTED && !isRowDiscarded(targetRow)) return targetRow;

        const sourceNote =
          sourceRow.pendingPloughedNote !== null && sourceRow.pendingPloughedNote !== undefined
            ? sourceRow.pendingPloughedNote
            : (sourceRow.ploughed_under_note ?? null);

        const savedNote = targetRow.ploughed_under_note ?? null;
        const newPending = sourceNote === savedNote ? null : sourceNote;

        let nextRow = { ...targetRow, pendingPloughedNote: newPending };

        if (targetStatus === STATUS.PLANTED && sourceNote) {
          const base = ensurePendingDate({ ...nextRow, pendingUnharvest: false });
          nextRow = {
            ...base,
            pendingYield: -1,
            ...(showWeight ? { pendingWeight: -1 } : {}),
          };
        }

        fillUpdateRef.current = true;
        return nextRow;
      }

      // --- discard reason ---
      if (columnKey === "pendingPloughedReasons") {
        if (targetRow.pendingUnharvest) return targetRow;
        const targetStatus = getEffectiveStatus(targetRow);
        if (targetStatus !== STATUS.PLANTED && !isRowDiscarded(targetRow)) return targetRow;

        const sourceIds =
          sourceRow.pendingPloughedReasons !== null &&
          sourceRow.pendingPloughedReasons !== undefined
            ? sourceRow.pendingPloughedReasons
            : (sourceRow.ploughed_under_reasons ?? []).map((r) => r.id);

        const backendIds = (targetRow.ploughed_under_reasons ?? []).map((r) => r.id);
        const sorted = (arr) => [...arr].sort().join(",");
        const isSameAsBackend = sorted(sourceIds) === sorted(backendIds);

        let nextRow = {
          ...targetRow,
          pendingPloughedReasons: isSameAsBackend ? null : sourceIds,
        };

        // Auto-transition planted rows to discarded when a reason is applied
        if (targetStatus === STATUS.PLANTED && sourceIds.length > 0) {
          const base = ensurePendingDate({ ...nextRow, pendingUnharvest: false });
          nextRow = {
            ...base,
            pendingYield: -1,
            ...(showWeight ? { pendingWeight: -1 } : {}),
          };
        }

        fillUpdateRef.current = true;
        return nextRow;
      }

      return targetRow;
    },
    [
      applyFilledNumber,
      ensurePendingDate,
      getEffectiveStatus,
      getEffectiveWeight,
      getEffectiveYield,
      harvestConfig,
      isRowDiscarded,
      showWeight,
    ],
  );

  const gridColumns = useHarvestEntryColumns({
    bayRowFilter,
    setBayRowFilter,
    handleBayRowFilterChange,
    bayRowTreeData,
    bayRowExpandedKeys,
    setBayRowExpandedKeys,
    statusOptions,
    statusColFilterValue,
    setStatusColFilterValue,
    renderStatusEditCell,
    renderDateEditCell,
    renderNumberEditCell,
    getEffectiveYield,
    getEffectiveWeight,
    getEffectiveStatus,
    statusPill,
    yieldLabel,
    weightLabel,
    showWeight,
    computeWidths,
    getGridPopupContainer,
    dropdownZIndex: DROPDOWN_Z,
    harvestConfig,
    isRowDiscarded,
    renderTubWeightEditCell,
    renderDiscardReasonEditCell,
    renderDiscardNoteEditCell,
  });

  const { borderGray, selectionColor, selectionFill, renderRow } =
    useHarvestEntryVisuals({ visibleRows });

  const dropdownProps = {
    siteMetadata,
    setSiteMetadata,
    currentOrg,
    setCurrentOrg,
    orgData,
    setOrgData,
    orgLoading,
    setOrgLoading,
    currentSite,
    setCurrentSite,
    siteData,
    setSiteData,
    siteLoading,
    setSiteLoading,
    currentCrop,
    setCurrentCrop,
    cropData,
    setCropData,
    cropLoading,
    setCropLoading,
    greenhouseData,
    setGreenhouseData,
    setDropdownLoading,
    searchParams,
    setSearchParams,
    settingDefaults,
    setSettingDefaults,
    setBlockLabel,
    blockLabel,
    greenHouseLabel,
    setGreenHouseLabel,
    selectedGreenhouses,
    setSelectedGreenhouses,
    registerHarvestRefresh,
  };

  const headerProps = {
    undo,
    redo,
    undoStack,
    redoStack,
    bulkActionsOpen,
    setBulkActionsOpen,
    getGridPopupContainer,
    DROPDOWN_Z,
    selectedRowIds,
    loading,
    dropdownLoading,
    bulkMenuItems,
    handleSaveVisible,
    savingAll,
    hasAnyPendingEdits,
    toggleFullscreen,
  };

  const gridProps = {
    gridRef,
    gridColumns,
    visibleRows,
    onRowsChange,
    onFill,
    onCellClick,
    onSelectedCellChange,
    renderRow,
    rowHasPendingChanges,
    sortColumns,
    setSortColumns,
    setSelectedRowIds,
    isFillDragging,
    setIsFillDragging,
    scheduleFillReset,
    isDesktop,
  };

  return (
    <HarvestEntryLayout
      contextHolder={contextHolder}
      onKeyDown={onKeyDown}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      gridWrapRef={gridWrapRef}
      isPseudoFullscreen={isPseudoFullscreen}
      isFullscreen={isFullscreen}
      FULLSCREEN_Z={FULLSCREEN_Z}
      GRID_MIN_HEIGHT={GRID_MIN_HEIGHT}
      gridHeight={gridHeight}
      borderGray={borderGray}
      selectionColor={selectionColor}
      selectionFill={selectionFill}
      dropdownProps={dropdownProps}
      headerProps={headerProps}
      gridProps={gridProps}
    />
  );
};

export default HarvestEntryPage;
